import { forwardRef, useCallback, useState } from "react";
import { Button } from "../../primitives";
import { Icon } from "../../primitives/icon";
import { getFileExtension } from "../../primitives/icon/document";
import EmailViewer from "./email";
import { JsonPreview } from "./json";
import { MarkdownPreview } from "./markdown";
import { SandboxedHtml } from "./html";
import { ImagePreview, ImagePreviewProps } from "./image";
import PDFViewer, { PDFViewerProps } from "./pdf";
import { VideoPreview, VideoPreviewProps } from "./video";

const PDF_MIME = "application/pdf";
const IMAGE_MIME = "image/*";
const VIDEO_MIME = "video/*";

enum SupportedExtension {
  PDF = "pdf",
  EMAIL = "eml",
}

export const isSupportedPreviewFileName = (
  filename: string,
): filename is `${string}.${SupportedExtension}` => {
  const ext = getFileExtension(filename);

  return Object.values(SupportedExtension).includes(ext as SupportedExtension);
};

type UnsupportedFileViewerProps = {
  file: File;
  displayname: string;
};

const UnsupportedFileViewer = ({
  file,
  displayname,
}: UnsupportedFileViewerProps) => {
  const [downloading, setDownloading] = useState(false);

  const downloadFile = useCallback(async () => {
    if (downloading) return;

    let successTimeoutId: ReturnType<typeof setTimeout>;
    if (downloading) return;
    setDownloading(true);

    const blob = file;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = displayname;
    a.click();
    URL.revokeObjectURL(url);
    setDownloading(true);

    setDownloading(false);
    successTimeoutId = setTimeout(() => {
      setDownloading(false);
    }, 3500);

    return () => {
      if (successTimeoutId!) clearTimeout(successTimeoutId);
    };
  }, []);

  return (
    <div className="flex p-8 justify-center items-center flex-col gap-8">
      <span>We can't load a preview for this type of file.</span>
      <Button
        className="min-w-max whitespace-nowrap"
        disabled={downloading}
        size="md"
        onClick={downloadFile}
        variant="gradient"
      >
        <Icon className="size-full" type="Download" />
        Download {displayname}
      </Button>
    </div>
  );
};

/** Content types FilePreview renders inline-as-text (vs the file/download path). */
export const SUPPORTED_TEXT_CONTENT_TYPES = new Set([
  "text/html",
  "text/markdown",
  "text/x-markdown",
  "application/json",
]);

/**
 * Inline-text input to {@link FilePreview}. Use this when the content is already
 * loaded as a string (e.g. a workflow artefact's inline body) rather than a
 * binary `File`. FilePreview dispatches it on `contentType` — presentationally,
 * it does not fetch/read anything.
 */
type InlineContentProps = {
  contentType: string;
  text: string;
  /** Title for the sandboxed iframe (HTML); defaults to "preview". */
  title?: string;
};

function InlineContent({ contentType, text, title }: InlineContentProps) {
  switch (contentType) {
    case "text/html":
      return <SandboxedHtml html={text} title={title ?? "preview"} />;
    case "text/markdown":
    case "text/x-markdown":
      return <MarkdownPreview markdown={text} />;
    case "application/json":
      return <JsonPreview json={text} />;
    default:
      return (
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-sm bg-gray-50 px-3 py-2 text-[12px] text-gray-800">
          {text || "(empty)"}
        </pre>
      );
  }
}

type FileTypeProps<T extends string, P extends {}> = Omit<P, "file"> & {
  file: Omit<File, "type"> & { type: T };
};
type PreviewComponentProps =
  | FileTypeProps<typeof PDF_MIME, PDFViewerProps>
  | FileTypeProps<typeof IMAGE_MIME, ImagePreviewProps>
  | FileTypeProps<typeof VIDEO_MIME, VideoPreviewProps>
  | InlineContentProps
  | UnsupportedFileViewerProps;

/**
 * The shared preview surface. Renders either a binary `File` (PDF, image, …) or
 * already-loaded inline text (`{ contentType, text }`), dispatched on type. Add
 * a new previewable type by adding a branch here so every caller gets it. Always
 * presentational — it never loads its own data; the caller passes it in.
 */
export const FilePreview = forwardRef<HTMLDivElement, PreviewComponentProps>(
  (props, ref) => {
    // Inline-text variant: content already resolved to a string.
    if ("text" in props) return <InlineContent {...props} />;

    const { file } = props;

    if (file.type === PDF_MIME)
      return (
        <PDFViewer
          ref={ref}
          {...(props as FileTypeProps<typeof PDF_MIME, PDFViewerProps>)}
        />
      );
    const [mimeType] = file.type?.split("/");

    if (`${mimeType}/*` === IMAGE_MIME)
      return (
        <ImagePreview {...(props as FileTypeProps<typeof IMAGE_MIME, ImagePreviewProps>)} />
      );

    if (`${mimeType}/*` === VIDEO_MIME)
      return (
        <VideoPreview {...(props as FileTypeProps<typeof VIDEO_MIME, VideoPreviewProps>)} />
      );

    return <UnsupportedFileViewer {...(props as UnsupportedFileViewerProps)} />;
  }
);

export {
  EmailViewer,
  JsonPreview,
  MarkdownPreview,
  PDFViewer,
  SandboxedHtml,
  UnsupportedFileViewer,
  VideoPreview,
};
